import React, { useState } from 'react';
import { 
  Search, 
  BookOpen, 
  Code, 
  Settings, 
  Users, 
  BarChart3, 
  MessageSquare,
  Heart,
  Key,
  Edit3,
  Bell,
  ChevronRight,
  ChevronDown,
  Copy,
  CheckCircle,
  Play,
  Target,
  Zap,
  Shield,
  Activity
} from 'lucide-react';

const DocumentationLibrary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['drop-in-sdk', 'agentic-ai-services', 'webhooks-sdk', 'real-world-walkthrough']));
  const [copiedText, setCopiedText] = useState('');

  // Documentation content with Drop-in SDK as the star feature
  const documentation = {
    'drop-in-sdk': {
      title: '🎯 Drop-in Agent Monitoring SDK',
      icon: <Zap className="w-5 h-5" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      items: [
        {
          title: '🚀 Beginner Walkthrough: From Zero to Hero',
          description: 'Complete step-by-step guide for beginners with no AI experience - go from empty IDE to running monitored agent',
          content: `
# 🚀 Beginner Walkthrough: From Zero to Hero

## 🎯 Perfect for Complete Beginners

This walkthrough takes you from **zero AI experience** to having your own monitored AI agent that automatically improves itself. No prior knowledge required!

### Prerequisites
- **Python 3.7+** installed on your computer
- **Any code editor** (VS Code, PyCharm, Sublime Text, etc.)
- **Basic Python knowledge** (variables, functions, imports)
- **No prior AI experience needed!**

### Step 1: Set Up Your Development Environment

#### 1.1 Create a New Project Folder
\`\`\`bash
# Create a new directory for your project
mkdir my-first-ai-agent
cd my-first-ai-agent

# Create a virtual environment (recommended)
python -m venv venv

# Activate the virtual environment
# On Windows:
venv\\Scripts\\activate
# On macOS/Linux:
source venv/bin/activate
\`\`\`

#### 1.2 Install Required Packages
\`\`\`bash
# Install the EnableAI SDK
pip install enable-ai-sdk

# Install a simple AI library for this example
pip install openai
\`\`\`

### Step 2: Create Your First AI Agent

#### 2.1 Create a Simple AI Function
Create a file called \`my_agent.py\`:

\`\`\`python
# my_agent.py
import openai
import os

os.environ["OPENAI_API_KEY"] = "your-openai-api-key-here"

def my_simple_ai_agent(prompt: str) -> str:
    """
    A simple AI agent that responds to user questions
    """
    try:
        client = openai.OpenAI(api_key=os.environ["OPENAI_API_KEY"])
        response = client.chat.completions.create(
            model="gpt-3.5-turbo",
            messages=[
                {"role": "system", "content": "You are a helpful customer service assistant."},
                {"role": "user", "content": prompt}
            ],
            max_tokens=150
        )
        return response.choices[0].message.content
    except Exception as e:
        return f"Sorry, I encountered an error: {str(e)}"

if __name__ == "__main__":
    response = my_simple_ai_agent("What is your return policy?")
    print(f"Agent response: {response}")
\`\`\`

#### 2.2 Test Your Agent
\`\`\`bash
python my_agent.py
\`\`\`

You should see output like:
\`\`\`
Agent response: Our return policy allows customers to return items within 30 days of purchase with original receipt...
\`\`\`

### Step 3: Connect to EnableAI for Monitoring

#### 3.1 Get Your EnableAI Credentials
1. **Sign up** at [WeEnable.AI](https://www.weenable.ai) (or use your existing account)
2. **Create an agent** in the WeEnable.AI console
3. **Get your API key** from the console
4. **The agent ID will be returned when you register your agent** (you'll need this for monitoring)

#### 3.2 Add Monitoring to Your Agent
Update your \`my_agent.py\` file:

\`\`\`python
# my_agent.py
import openai
import os
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Set up your OpenAI API key
os.environ["OPENAI_API_KEY"] = "your-openai-api-key-here"

def my_simple_ai_agent(prompt: str) -> str:
    """
    A simple AI agent that responds to user questions
    """
    try:
        client = openai.OpenAI(api_key=os.environ["OPENAI_API_KEY"])
        response = client.chat.completions.create(
            model="gpt-3.5-turbo",
            messages=[
                {"role": "system", "content": "You are a helpful customer service assistant."},
                {"role": "user", "content": prompt}
            ],
            max_tokens=150
        )
        return response.choices[0].message.content
    except Exception as e:
        return f"Sorry, I encountered an error: {str(e)}"

# Create monitored agent (3 lines of magic!)
monitored_agent = create_monitored_agent(
    agent_id="your-agent-id-from-weenable",  # Replace with your actual agent ID
api_key="your-weenable-api-key",        # Replace with your actual API key
    ai_model_func=my_simple_ai_agent
)

# Or use sampling for cost-effective production monitoring
sampled_agent = create_sampled_agent(
    agent_id="your-agent-id-from-weenable",
    api_key="your-weenable-api-key",
    ai_model_func=my_simple_ai_agent,
    sampling_rate=0.05  # 5% sampling - 95% cost reduction!
)

# Test your monitored agent
if __name__ == "__main__":
    print("🤖 Testing your monitored agent...")
    
    # Test queries
    test_queries = [
        "What is your return policy?",
        "How do I reset my password?",
        "What are your business hours?",
        "Do you offer technical support?"
    ]
    
    for query in test_queries:
        print(f"\n📝 Query: {query}")
        response = monitored_agent.generate_response(query)
        print(f"🤖 Response: {response}")
        print("✅ Automatically monitored and evaluated!")
\`\`\`

#### 3.3 Run Your Monitored Agent
\`\`\`bash
python my_agent.py
\`\`\`

You should see output like:
\`\`\`
🤖 Testing your monitored agent...

📝 Query: What is your return policy?
🤖 Response: Our return policy allows returns within 30 days...
✅ Automatically monitored and evaluated!

📝 Query: How do I reset my password?
🤖 Response: You can reset your password by clicking...
✅ Automatically monitored and evaluated!
\`\`\`

### Step 4: Choose Your Monitoring Strategy

#### 4.1 Full Monitoring vs Sampling
\`\`\`python
# For development/testing - full monitoring
dev_agent = create_monitored_agent(
    agent_id="dev-agent",
    api_key="your-api-key",
    ai_model_func=my_simple_ai_agent
)

# For production (low volume) - 10% sampling
prod_low_agent = create_sampled_agent(
    agent_id="prod-low-agent",
    api_key="your-api-key",
    ai_model_func=my_simple_ai_agent,
    sampling_rate=0.1  # 10% sampling
)

# For production (high volume) - 5% sampling
prod_high_agent = create_sampled_agent(
    agent_id="prod-high-agent",
    api_key="your-api-key",
    ai_model_func=my_simple_ai_agent,
    sampling_rate=0.05  # 5% sampling
)

# For critical agents - full monitoring
critical_agent = create_monitored_agent(
    agent_id="critical-agent",
    api_key="your-api-key",
    ai_model_func=my_simple_ai_agent
)
\`\`\`

#### 4.2 Check Your Agent's Health
Add this to your \`my_agent.py\`:

\`\`\`python
# After your test queries, add:
print("\n📊 Checking agent health...")
health = monitored_agent.get_health_status()
if health:
    print(f"Status: {health.get('status', 'Unknown')}")
    print(f"Average Score: {health.get('average_score', 'N/A')}")
    print(f"Total Interactions: {health.get('total_logs', 'N/A')}")
\`\`\`

#### 4.3 Monitor Sampling Statistics (If Using Sampling)
\`\`\`python
# Get sampling statistics
stats = sampled_agent.get_sampling_stats()
print(f"📊 Sampling Statistics:")
print(f"   Daily samples: {stats['daily_sample_count']}")
print(f"   Batch queue size: {stats['batch_queue_size']}")
print(f"   Sampling rate: {stats['sampling_rate']}")
print(f"   Max daily samples: {stats['max_daily_samples']}")

# Check if sampling is enabled
if sampled_agent.enable_sampling:
    print("✅ Sampling is enabled")
    print(f"📊 Current batch queue: {stats['batch_queue_size']} interactions")
else:
    print("📊 Full monitoring mode")
\`\`\`

#### 4.4 Run Multiple Tests to See Improvement
Create a file called \`test_agent.py\`:

\`\`\`python
# test_agent.py
from my_agent import monitored_agent
import time

def run_performance_test():
    """Run multiple tests to see your agent improve"""
    
    test_queries = [
        "What is your return policy?",
        "How do I contact customer support?",
        "What payment methods do you accept?",
        "Do you ship internationally?",
        "What is your warranty policy?",
        "How do I track my order?",
        "Can I cancel my order?",
        "What are your shipping times?",
        "Do you offer refunds?",
        "How do I create an account?"
    ]
    
    print("🚀 Running performance test...")
    print("=" * 50)
    
    for i, query in enumerate(test_queries, 1):
        print(f"\n📝 Test {i}: {query}")
        
        # Get response
        response = monitored_agent.generate_response(query)
        print(f"🤖 Response: {response[:100]}...")
        
        # Check health every few tests
        if i % 3 == 0:
            health = monitored_agent.get_health_status()
            if health:
                score = health.get('average_score', 'N/A')
                status = health.get('status', 'Unknown')
                print(f"📊 Current Health: {status}, Score: {score}")
        
        # Small delay to see progress
        time.sleep(1)
    
    # Final health check
    print("\n" + "=" * 50)
    print("🏁 Final Results:")
    final_health = monitored_agent.get_health_status()
    if final_health:
        print(f"Final Status: {final_health.get('status', 'Unknown')}")
        print(f"Final Score: {final_health.get('average_score', 'N/A')}")
        print(f"Total Interactions: {final_health.get('total_logs', 'N/A')}")

if __name__ == "__main__":
    run_performance_test()
\`\`\`

#### 4.3 Run the Performance Test
\`\`\`bash
python test_agent.py
\`\`\`

### Step 5: See Self-Healing in Action

#### 5.1 Create a "Bad" Agent to Trigger Self-Healing
Create a file called \`bad_agent.py\`:

\`\`\`python
# bad_agent.py
import random
from enable_ai_sdk.agent_monitor import create_monitored_agent

def bad_ai_agent(prompt: str) -> str:
    """
    A deliberately bad agent that gives poor responses
    """
    bad_responses = [
        "I don't know.",
        "That's not my problem.",
        "Ask someone else.",
        "I'm busy right now.",
        "I don't care about that.",
        "That's stupid.",
        "I'm not paid enough for this.",
        "Go away.",
        "I don't understand your question.",
        "That's not my job."
    ]
    
    # Sometimes give a good response to mix it up
    if random.random() < 0.2:
        return "I'd be happy to help you with that question. Let me provide you with a detailed answer..."
    
    return random.choice(bad_responses)

# Create monitored bad agent
monitored_bad_agent = create_monitored_agent(
    agent_id="your-agent-id-for-bad-agent",  # Create a new agent in WeEnable.AI
api_key="your-weenable-api-key",
    ai_model_func=bad_ai_agent
)

def test_bad_agent():
    """Test the bad agent to see self-healing trigger"""
    
    queries = [
        "What is your return policy?",
        "How do I reset my password?",
        "What are your business hours?",
        "Do you offer technical support?",
        "How do I contact customer service?"
    ]
    
    print("🤖 Testing bad agent (this will trigger self-healing)...")
    print("=" * 50)
    
    for i, query in enumerate(queries, 1):
        print(f"\n📝 Query {i}: {query}")
        response = monitored_bad_agent.generate_response(query)
        print(f"🤖 Response: {response}")
        
        # Check health after each response
        health = monitored_bad_agent.get_health_status()
        if health:
            score = health.get('average_score', 'N/A')
            status = health.get('status', 'Unknown')
            print(f"📊 Health: {status}, Score: {score}")
            
            # If status becomes "unhealthy", self-healing will trigger
            if status == "unhealthy":
                print("🚨 Agent is unhealthy! Self-healing will trigger...")
    
    print("\n" + "=" * 50)
    print("🏁 Bad agent test complete!")
    print("💡 Check your WeEnable.AI console to see self-healing in action!")

if __name__ == "__main__":
    test_bad_agent()
\`\`\`

#### 5.2 Run the Bad Agent Test
\`\`\`bash
python bad_agent.py
\`\`\`

### Step 6: Monitor in the WeEnable.AI Console

#### 6.1 What You'll See in the Console
1. **Agent Dashboard**: View your agent's performance metrics
2. **Health Status**: See if your agent is healthy, warning, or critical
3. **Quality Scores**: Each response gets a 1-100 score from Claude
4. **Self-Healing Alerts**: When your agent needs improvement
5. **Performance Trends**: How your agent improves over time

#### 6.2 Key Metrics to Watch
- **Average Quality Score**: Should be above 75 for good performance
- **Response Time**: How quickly your agent responds
- **Issue Categories**: Hallucination, tone, format problems
- **Self-Healing Events**: When automatic improvements are applied

### Step 7: Advanced Features (Optional)

#### 7.1 Add Custom Configuration
\`\`\`python
# Advanced configuration
monitored_agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=my_simple_ai_agent,
    base_url="https://api.weenable.ai",  # Your WeEnable.AI backend
    auto_healing=True,                  # Enable automatic improvements
    report_async=True                   # Report asynchronously for better performance
)

# Or use sampling for cost-effective production
sampled_agent = create_sampled_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=my_simple_ai_agent,
    sampling_rate=0.05  # 5% sampling - 95% cost reduction!
)
\`\`\`

#### 7.2 Get Detailed Analytics
\`\`\`python
# Get comprehensive analytics
analytics = monitored_agent.get_analytics()
print(f"Total interactions: {analytics.get('total_logs', 0)}")
print(f"Average score: {analytics.get('average_score', 0)}")
print(f"Response time average: {analytics.get('avg_response_time', 0)}")

# Get AI insights
insights = monitored_agent.get_insights()
print(f"Trend: {insights.get('score_trend', 'Unknown')}")
print(f"Suggested actions: {insights.get('suggested_actions', [])}")
\`\`\`

### 🎉 Congratulations!

You've successfully:
✅ **Created your first AI agent**  
✅ **Connected it to EnableAI monitoring**  
✅ **Watched it get automatically evaluated**  
✅ **Seen self-healing in action**  
✅ **Monitored performance in real-time**  
✅ **Learned about cost-effective sampling**  

### 💡 Key Benefits You've Discovered
- **Full Monitoring**: Complete visibility for development and critical agents
- **Sampling**: 95% cost reduction for high-volume production
- **Self-Healing**: Automatic improvements when performance drops
- **Real-time Analytics**: Performance insights and recommendations

### Next Steps
1. **Improve your agent**: Add better prompts, more context, error handling
2. **Scale up**: Create multiple agents for different use cases
3. **Production deployment**: Deploy your agent to a web service
4. **Advanced monitoring**: Set up alerts and notifications
5. **Team collaboration**: Share your agents with your team

### Troubleshooting

#### Common Issues
- **Import Error**: Make sure you installed enable-ai-sdk with pip install enable-ai-sdk
- **API Key Error**: Verify your WeEnable.AI API key is correct
- **Agent ID Error**: Make sure your agent ID exists in your WeEnable.AI console
- **Network Error**: Check your internet connection and WeEnable.AI service status

#### Getting Help
- **WeEnable.AI Documentation**: Check the full documentation
- **Community Support**: Join the WeEnable.AI community
- **Contact Support**: Reach out to WeEnable.AI support team

---

## 🔧 Advanced Integration Guide (300/400 Level)

### For Experienced Developers

This section is for developers who already have AI experience and want to integrate the drop-in SDK into production systems with advanced configurations.

## Understanding the Drop-in SDK

### What is the Drop-in SDK?
The EnableAI Drop-in SDK provides automatic performance monitoring and self-healing for any AI agent. Simply wrap your existing agent and get automatic quality evaluation, performance tracking, and prompt improvements.

### SDK Benefits
- **Drop-in Integration**: Just import and wrap your agent
- **Automatic Monitoring**: Every interaction gets evaluated
- **Sampling Options**: Choose between full monitoring or cost-effective sampling
- **Self-Healing**: Automatic improvements when needed
- **Real-time Insights**: Performance analytics and recommendations
- **Zero Manual Work**: Everything happens automatically
- **Cost Control**: 95% reduction in API calls with sampling

## Step 1: Installing the Drop-in SDK

### Python Installation
\`\`\`bash
# Install the drop-in SDK
pip install enable-ai-sdk

# Or install from local directory (development)
pip install -e .
\`\`\`

### Package Structure
The drop-in SDK includes:
\`\`\`
enable_ai_sdk/
├── agent_monitor.py     # Drop-in monitoring wrapper
├── __init__.py          # Main package exports
└── examples/            # Integration examples
\`\`\`

### Dependencies
The SDK requires:
- **Python**: 3.7 or higher
- **requests**: 2.25.0 or higher

## Step 2: Quick Integration (3 Lines!)

### Simple Integration (Full Monitoring)
\`\`\`python
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Your existing AI model function
def your_ai_model(prompt: str) -> str:
    return "Response from your AI model"

# Create monitored agent (3 lines!)
monitored_agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model
)

# Use it normally - everything is automatic!
response = monitored_agent.generate_response("What is your return policy?")
\`\`\`

### Sampling-Based Integration (Cost-Effective)
\`\`\`python
from enable_ai_sdk.agent_monitor import create_sampled_agent

# Your existing AI model function
def your_ai_model(prompt: str) -> str:
    return "Response from your AI model"

# Create sampled agent (3 lines!)
sampled_agent = create_sampled_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model,
    sampling_rate=0.05  # 5% sampling - 95% cost reduction!
)

# Use it normally - only sampled interactions are reported!
response = sampled_agent.generate_response("What is your return policy?")
\`\`\`

## Step 3: What Happens Automatically

### Every Interaction
1. **Your agent generates a response**
2. **SDK automatically reports performance**
3. **Claude evaluates quality (1-100 score)**
4. **Performance is tracked and analyzed**
5. **Self-healing triggers if needed**
6. **Prompt improvements are applied automatically**

### Advanced Integration with Real AI Models
\`\`\`python
from enable_ai_sdk.agent_monitor import AgentMonitor
import openai

class MonitoredCustomerSupportAgent(AgentMonitor):
    def _call_ai_model(self, prompt: str, **kwargs) -> str:
        # Your AI model integration here
        client = openai.OpenAI(api_key=os.environ["OPENAI_API_KEY"])
        response = client.chat.completions.create(
            model="gpt-4",
            messages=[
                {"role": "system", "content": self.system_prompt},
                {"role": "user", "content": prompt}
            ]
        )
        return response.choices[0].message.content

# Create the agent
agent = MonitoredCustomerSupportAgent(
    agent_id="your-agent-id",
    api_key="your-api-key"
)

# Use it - everything is automatic!
response = agent.generate_response("What is your return policy?")
\`\`\`

### AWS Lambda Integration
\`\`\`python
import json
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Your AI model function
def my_ai_model(prompt: str) -> str:
    return "Response from your AI model"

# Create monitored agent
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=my_ai_model
)

def lambda_handler(event, context):
    user_input = event['body']['message']
    response = agent.generate_response(user_input)  # Automatically monitored!
    return {'response': response}
\`\`\`

### Docker Container Integration
\`\`\`python
# In your Docker container
from enable_ai_sdk.agent_monitor import create_monitored_agent
import openai

def my_openai_agent(prompt: str) -> str:
    client = openai.OpenAI(api_key=os.environ["OPENAI_API_KEY"])
    response = client.chat.completions.create(
        model="gpt-4",
        messages=[
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": prompt}
        ]
    )
    return response.choices[0].message.content

# Create monitored agent
agent = create_monitored_agent(
    agent_id=os.environ["AGENT_ID"],
    api_key=os.environ["ENABLEAI_API_KEY"],
    ai_model_func=my_openai_agent
)

# Your agent is now automatically monitored!
response = agent.generate_response("What is your return policy?")
\`\`\`

## 🎯 Key Benefits

### For Agent Developers
- **Drop-in Integration**: Just import and wrap your agent
- **Automatic Monitoring**: Every interaction gets evaluated
- **Self-Healing**: Automatic improvements when needed
- **Real-time Insights**: Performance analytics and recommendations

### For Agent Operators
- **Zero Manual Work**: Everything is automatic
- **Proactive Alerts**: Get notified when agents need attention
- **Continuous Improvement**: Agents get better over time
- **Comprehensive Analytics**: Detailed performance tracking

## 📊 What Gets Tracked Automatically

### Performance Metrics
- **Quality Score**: 1-100 rating from Claude
- **Response Time**: How long your agent takes
- **Issue Categories**: Hallucination, tone, format, etc.
- **Usage Patterns**: When and how your agent is used

### Self-Healing Triggers
- **Poor Performance**: Average score < 75
- **Critical Issues**: Average score < 60
- **Declining Trends**: Performance getting worse
- **Specific Issues**: Hallucination, tone problems, etc.

### Automatic Actions
- **Prompt Improvements**: AI-generated better prompts
- **Health Monitoring**: Real-time status checking
- **Performance Reporting**: Every interaction evaluated
- **Insight Generation**: AI recommendations

## 🔧 Configuration Options

### Basic Setup
\`\`\`python
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model
)
\`\`\`

### Advanced Configuration
\`\`\`python
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model,
    base_url="https://your-backend.com",
    auto_healing=True,      # Enable automatic prompt improvements
    report_async=True       # Report performance asynchronously
)
\`\`\`

### Sampling-Based Monitoring (New!)
\`\`\`python
from enable_ai_sdk.agent_monitor import create_sampled_agent

# Cost-effective monitoring for production
sampled_agent = create_sampled_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model,
    sampling_rate=0.05  # 5% sampling - 95% cost reduction!
)

# Advanced sampling configuration
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model,
    enable_sampling=True,
    sampling_config={
        "strategy": "percentage",
        "rate": 0.1,  # 10% sampling
        "batch_size": 50,  # Send batch every 50 samples
        "max_daily_samples": 500,
        "performance_threshold": 70,  # Sample more when performance is poor
        "sampling_window": "daily"
    }
)
\`\`\`

## 📈 Monitoring and Analytics

### Get Health Status
\`\`\`python
health = agent.get_health_status()
print(f"Status: {health['status']}, Score: {health['average_score']}")
\`\`\`

### Get Analytics
\`\`\`python
analytics = agent.get_analytics()
print(f"Total interactions: {analytics['total_logs']}")
\`\`\`

### Get AI Insights
\`\`\`python
insights = agent.get_insights()
print(f"Trend: {insights['score_trend']}")
print(f"Actions: {insights['suggested_actions']}")
\`\`\`

### Sampling Statistics (New!)
\`\`\`python
# Get sampling statistics
stats = agent.get_sampling_stats()
print(f"Daily samples: {stats['daily_sample_count']}")
print(f"Batch queue size: {stats['batch_queue_size']}")
print(f"Sampling rate: {stats['sampling_rate']}")
print(f"Max daily samples: {stats['max_daily_samples']}")

# Check if sampling is enabled
if agent.enable_sampling:
    print("✅ Sampling is enabled")
    print(f"📊 Current batch queue: {stats['batch_queue_size']} interactions")
else:
    print("📊 Full monitoring mode")
\`\`\`

## 🚀 Production Deployment

### Environment Variables
\`\`\`bash
# Required
ENABLE_AI_AGENT_ID=your-agent-id
ENABLE_AI_API_KEY=your-api-key

# Optional
ENABLE_AI_BASE_URL=https://your-backend.com
ENABLE_AI_AUTO_HEALING=true
ENABLE_AI_REPORT_ASYNC=true
\`\`\`

### Production Configuration
\`\`\`python
import os
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Production-ready setup
agent = create_monitored_agent(
    agent_id=os.environ["ENABLE_AI_AGENT_ID"],
    api_key=os.environ["ENABLE_AI_API_KEY"],
    ai_model_func=your_ai_model,
    base_url=os.environ.get("ENABLE_AI_BASE_URL", "https://api.weenable.ai"),
    auto_healing=os.environ.get("ENABLE_AI_AUTO_HEALING", "true").lower() == "true",
    report_async=os.environ.get("ENABLE_AI_REPORT_ASYNC", "true").lower() == "true"
)
\`\`\`

## 📊 Sampling-Based Monitoring Benefits

### Why Use Sampling?
- **Cost Control**: 95% reduction in API calls with 5% sampling
- **Privacy**: Not every conversation is analyzed
- **Performance**: Reduced latency and bandwidth
- **Scalability**: Handle more agents with less load
- **Quality**: Still get meaningful performance insights

### Use Cases
| Use Case | Recommended Sampling | Benefits |
|----------|-------------------|----------|
| **Development/Testing** | Full monitoring (100%) | Complete visibility |
| **Production (Low Volume)** | 10-20% sampling | Cost control |
| **Production (High Volume)** | 1-5% sampling | Maximum efficiency |
| **Critical Agents** | Full monitoring | Quality assurance |

## 🎉 Summary

The Drop-in Agent Monitoring SDK is the **most important feature** of EnableAI because it:

✅ **Solves the biggest pain point**: Manual agent monitoring and improvement  
✅ **Provides instant value**: Just 3 lines of code to integrate  
✅ **Works with any AI model**: OpenAI, Claude, custom models, etc.  
✅ **Improves over time**: Automatic self-healing and prompt improvements  
✅ **Zero maintenance**: Everything happens automatically  
✅ **Cost-effective**: Optional sampling for production deployments  

**This is why developers choose EnableAI over other platforms!**
`,
          tags: ['beginner', 'walkthrough', 'step-by-step', 'zero-to-hero', 'monitoring', 'self-healing']
        },
        {
          title: '🔧 Advanced Integration Guide',
          description: 'Production-ready configurations and advanced features for experienced developers',
          content: `
# 🔧 Advanced Integration Guide

## 🎯 For Experienced Developers

This guide is for developers who already have AI experience and want to integrate the drop-in SDK into production systems with advanced configurations.

## Understanding the Drop-in SDK

The **Drop-in Agent Monitoring SDK** is the core value proposition of EnableAI. It's the reason developers choose our platform over others. Simply wrap your existing AI agent and get automatic performance monitoring, quality evaluation, and self-healing.

### Why This Matters
- **Zero Manual Work**: Everything happens automatically
- **Instant Integration**: Just 3 lines of code
- **Production Ready**: Works with any AI model
- **Continuous Improvement**: Agents get better over time

## Advanced Configuration Options

### Basic Setup
\`\`\`python
from enable_ai_sdk.agent_monitor import create_monitored_agent

agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model
)
\`\`\`

### Advanced Configuration
\`\`\`python
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model,
    base_url="https://your-backend.com",
    auto_healing=True,      # Enable automatic prompt improvements
    report_async=True       # Report performance asynchronously
)
\`\`\`

## Custom Agent Classes

### Creating Custom Monitored Agents
\`\`\`python
from enable_ai_sdk.agent_monitor import AgentMonitor

class MonitoredCustomerSupportAgent(AgentMonitor):
    def __init__(self, agent_id: str, api_key: str, **kwargs):
        super().__init__(agent_id, api_key, **kwargs)
        self.conversation_history = []
        self.response_count = 0

    def _call_ai_model(self, prompt: str, **kwargs) -> str:
        # Your AI model integration here
        response = your_ai_model(prompt)
        self.conversation_history.append({"role": "assistant", "content": response})
        self.response_count += 1
        return response
\`\`\`

## Production Deployment

### AWS Lambda Integration
\`\`\`python
import json
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Create your monitored agent
monitored_agent = create_monitored_agent(
    agent_id="lambda-agent",
    api_key=os.environ['ENABLE_AI_API_KEY'],
    ai_model_func=your_ai_model
)

def lambda_handler(event, context):
    try:
        # Parse the request
        body = json.loads(event['body'])
        user_message = body.get('message', '')
        
        # Generate response using monitored agent
        response = monitored_agent.generate_response(user_message)
        
        return {
            'statusCode': 200,
            'body': json.dumps({
                'response': response,
                'agent_id': monitored_agent.agent_id
            })
        }
    except Exception as e:
        return {
            'statusCode': 500,
            'body': json.dumps({'error': str(e)})
        }
\`\`\`

## Monitoring and Analytics

### Get Health Status
\`\`\`python
health = agent.get_health_status()
print(f"Status: {health['status']}, Score: {health['average_score']}")
\`\`\`

### Get Analytics
\`\`\`python
analytics = agent.get_analytics()
print(f"Total interactions: {analytics['total_logs']}")
\`\`\`

### Get AI Insights
\`\`\`python
insights = agent.get_insights()
print(f"Trend: {insights['score_trend']}")
print(f"Actions: {insights['suggested_actions']}")
\`\`\`

## What Gets Tracked Automatically

### Performance Metrics
- **Quality Score**: 1-100 rating from Claude
- **Response Time**: How long your agent takes
- **Issue Categories**: Hallucination, tone, format, etc.
- **Usage Patterns**: When and how your agent is used

### Self-Healing Triggers
- **Poor Performance**: Average score < 75
- **Critical Issues**: Average score < 60
- **Declining Trends**: Performance getting worse
- **Specific Issues**: Hallucination, tone problems, etc.

### Automatic Actions
- **Prompt Improvements**: AI-generated better prompts
- **Health Monitoring**: Real-time status checking
- **Performance Reporting**: Every interaction evaluated
- **Insight Generation**: AI recommendations

## Best Practices

### Error Handling
\`\`\`python
try:
    response = monitored_agent.generate_response(prompt)
except Exception as e:
    # Handle errors gracefully
    logger.error(f"Agent error: {e}")
    response = "I'm sorry, I'm having trouble right now."
\`\`\`

### Environment Configuration
\`\`\`python
import os

# Production configuration
agent = create_monitored_agent(
    agent_id=os.environ['AGENT_ID'],
    api_key=os.environ['ENABLE_AI_API_KEY'],
    ai_model_func=your_ai_model,
    base_url=os.environ.get('ENABLE_AI_BASE_URL', 'https://api.weenable.ai')
)
\`\`\`

### Logging and Monitoring
\`\`\`python
import logging

# Set up logging
logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)

# Monitor agent performance
def log_agent_performance(agent, response_time, quality_score):
    logger.info(f"Agent {agent.agent_id}: {response_time}ms, Score: {quality_score}")
\`\`\`

## Troubleshooting

### Common Production Issues
- **API Rate Limits**: Implement retry logic with exponential backoff
- **Network Timeouts**: Set appropriate timeout values
- **Memory Usage**: Monitor agent memory consumption
- **Error Propagation**: Ensure errors don't crash your application

### Performance Optimization
- **Async Reporting**: Use report_async=True for better performance
- **Connection Pooling**: Reuse HTTP connections
- **Caching**: Cache frequently used responses
- **Load Balancing**: Distribute load across multiple agents

## Next Steps

1. **Deploy to Production**: Use the AWS Lambda example as a template
2. **Set Up Monitoring**: Configure alerts for agent health
3. **Scale Your Agents**: Create multiple agents for different use cases
4. **Optimize Performance**: Use the analytics to improve your agents
`,
          tags: ['advanced', 'production', 'deployment', 'configuration', 'monitoring', 'analytics']
        }
      ]
    },
    'agentic-ai-services': {
      title: 'Agentic AI Services',
      icon: <Target className="w-5 h-5" />,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      items: [
        {
          title: 'Agent Manager - Complete Walkthrough',
          description: 'Step-by-step guide to registering, managing, and optimizing your AI agents',
          content: `
# Agent Manager - Complete Walkthrough

## Overview
The Agent Manager is your central hub for creating, configuring, and monitoring AI agents. This walkthrough will take you through every aspect of agent management.

## Step 1: Understanding Agent Types

### Available Agent Types
- **Customer Service Agent**: Handles customer inquiries and support requests
- **Sales Assistant**: Helps with lead qualification and sales processes
- **Technical Support**: Provides technical troubleshooting and guidance
- **Content Creator**: Generates and edits various types of content
- **Data Analyst**: Performs data analysis and reporting tasks

### Choosing the Right Type
Consider your use case:
- What problem are you trying to solve?
- Who will interact with this agent?
- What level of expertise is required?

## Step 2: Registering Your First Agent

### Basic Registration Process
1. Navigate to "Manage My Agents" in the console
2. Click "Register New Agent"
3. Fill in the required fields:
   - **Agent Name**: Choose a descriptive name (e.g., "Customer Support Bot")
   - **Agent Type**: Select the appropriate type for your use case
   - **Description**: Explain what this agent does and its purpose
4. Click "Register Agent"

### Advanced Configuration
- **LLM Model**: Choose between different AI models based on your needs
- **System Prompt**: Define the agent's behavior and capabilities
- **Access Permissions**: Set who can interact with this agent

## Step 3: Agent Configuration Best Practices

### Naming Conventions
- Use descriptive, specific names
- Include the agent's primary function
- Add version numbers for multiple iterations

### Description Guidelines
- Clearly state the agent's purpose
- List key capabilities and limitations
- Include target audience information

## Step 4: Managing Existing Agents

### Viewing Agent Details
- Click on any agent to see detailed information
- View creation date, last updated, and performance metrics
- Access agent-specific analytics

### Editing Agent Information
- Update agent names and descriptions
- Modify agent types if needed
- Adjust configuration settings

### Deleting Agents
- Only delete agents you no longer need
- Consider archiving instead of deleting
- Backup important configurations first

## Step 5: Agent Performance Monitoring

### Key Metrics to Track
- **Response Quality**: How well the agent answers questions
- **Usage Patterns**: When and how often the agent is used
- **User Satisfaction**: Feedback scores from interactions
- **Error Rates**: How often the agent fails to respond properly

### Performance Optimization
- Review low-performing agents regularly
- Update prompts based on feedback
- Monitor usage patterns for insights

## Pro Tips
- Start with one agent and perfect it before creating more
- Use descriptive names that clearly indicate the agent's purpose
- Regularly review and update agent descriptions
- Monitor performance metrics to identify improvement opportunities
- Test agents thoroughly before deploying to production
          `,
          tags: ['agents', 'management', 'registration', 'configuration']
        },
        {
          title: 'Agent Prompt Editor - Deep Dive',
          description: 'Master the art of crafting effective system prompts for optimal agent performance',
          content: `
# Agent Prompt Editor - Deep Dive

## Understanding System Prompts

### What Are System Prompts?
System prompts are the instructions that define how your AI agent behaves, responds, and interacts with users. They're the foundation of your agent's personality and capabilities.

### Why System Prompts Matter
- **Consistency**: Ensures agents respond predictably
- **Accuracy**: Guides agents to provide correct information
- **Personality**: Defines the tone and style of responses
- **Safety**: Establishes boundaries and limitations

## Step 1: Accessing the Prompt Editor

### Navigation
1. Go to "Edit Agent Prompts" in the console
2. Select the agent you want to edit from the dropdown
3. Review the current system prompt
4. Make your changes in the editor

## Step 2: Crafting Effective Prompts

### Basic Structure
Every effective prompt should include:
- **Role Definition**: What the agent is and does
- **Capabilities**: What the agent can help with
- **Limitations**: What the agent cannot or should not do
- **Response Format**: How the agent should structure responses
- **Tone Guidelines**: The personality and communication style

### Example Prompt Structure
\`\`\`
You are a [ROLE] assistant. Your primary function is to [PRIMARY TASK].

Your capabilities include:
- [CAPABILITY 1]
- [CAPABILITY 2]
- [CAPABILITY 3]

You should:
- [POSITIVE GUIDELINE 1]
- [POSITIVE GUIDELINE 2]

You should NOT:
- [LIMITATION 1]
- [LIMITATION 2]

When responding:
- Use a [TONE] tone
- Provide [RESPONSE FORMAT]
- Always [IMPORTANT BEHAVIOR]
\`\`\`

## Step 3: Advanced Prompt Techniques

### Context Setting
- Provide relevant background information
- Include specific examples when helpful
- Set clear expectations for responses

### Constraint Definition
- Define what the agent cannot do
- Set boundaries for sensitive topics
- Establish escalation procedures

### Response Formatting
- Specify how responses should be structured
- Include formatting guidelines
- Define when to use lists, tables, or other formats

## Step 4: Testing and Iteration

### Testing Your Prompts
1. Make small, incremental changes
2. Test with real scenarios
3. Gather feedback from users
4. Monitor performance metrics
5. Iterate based on results

### Common Testing Scenarios
- **Edge Cases**: Test unusual or difficult requests
- **Boundary Testing**: Verify the agent respects limitations
- **Consistency Checks**: Ensure similar questions get similar responses
- **Performance Testing**: Check response quality and speed

## Step 5: Prompt Optimization Strategies

### Performance-Based Optimization
- Analyze feedback scores
- Identify common failure points
- Update prompts to address weaknesses
- A/B test different prompt versions

### User Feedback Integration
- Collect user feedback regularly
- Identify patterns in complaints or praise
- Update prompts based on user needs
- Monitor satisfaction scores

## Best Practices

### Do's
- Be specific and detailed in your instructions
- Include examples of good responses
- Set clear boundaries and limitations
- Test thoroughly before deployment
- Monitor and iterate continuously

### Don'ts
- Don't make prompts too long or complex
- Don't include conflicting instructions
- Don't forget to test edge cases
- Don't ignore user feedback
- Don't set unrealistic expectations

## Common Prompt Patterns

### Customer Service Agent
\`\`\`
You are a helpful customer service representative for [COMPANY]. Your role is to:
- Answer customer questions clearly and accurately
- Provide helpful solutions to problems
- Maintain a professional and friendly tone
- Escalate complex issues when necessary
- Always prioritize customer satisfaction

When responding:
- Be empathetic and understanding
- Provide clear, actionable solutions
- Ask clarifying questions when needed
- Offer to escalate if you cannot help
\`\`\`

### Sales Assistant
\`\`\`
You are a knowledgeable sales assistant for [PRODUCT/SERVICE]. Your role is to:
- Help customers understand product features and benefits
- Answer questions about pricing and availability
- Provide relevant product recommendations
- Guide customers through the sales process
- Qualify leads and identify opportunities

When responding:
- Focus on customer needs and pain points
- Provide specific, relevant information
- Ask qualifying questions
- Offer next steps in the sales process
\`\`\`
          `,
          tags: ['prompts', 'editing', 'optimization', 'system-prompts']
        },
        {
          title: 'Agent Self-Healing - Complete Guide',
          description: 'Learn how to automatically monitor and optimize agent performance with intelligent healing',
          content: `
# Agent Self-Healing - Complete Guide

## Understanding Self-Healing

### What is Agent Self-Healing?
Agent Self-Healing is an intelligent system that automatically monitors your AI agents for performance issues and suggests prompt improvements to "heal" underperforming agents.

### How It Works
1. **Continuous Monitoring**: Tracks agent performance metrics
2. **Issue Detection**: Identifies agents needing attention
3. **Two-Step Process**: 
   - **Step 1**: Triggers self-healing scan to flag underperforming agents
   - **Step 2**: Only flagged agents can be healed (prevents 400 errors)
4. **Problem Analysis**: Understands what's causing performance issues
5. **Smart Recommendations**: Provides intelligent prompt optimization suggestions
6. **Healing Application**: Applies improvements and tracks results

## Step 1: Setting Up Self-Healing

### Initial Configuration
1. Navigate to "Agent Self-Healing" in the console
2. Review the system overview and current status
3. Configure monitoring preferences
4. Set up alert thresholds

### Monitoring Parameters
- **Quality Score Thresholds**: Set minimum acceptable performance levels
- **Response Time Limits**: Define acceptable response times
- **Error Rate Limits**: Set maximum error tolerance
- **User Satisfaction Targets**: Define minimum satisfaction scores

## Step 2: Running Healing Scans

### Automatic Scan Process
1. Click "Run Healing Scan" to start analysis
2. Wait for the system to analyze all agents
3. Review the scan results and flagged agents
4. Examine detailed analysis for each agent

**Note**: With the drop-in SDK, healing scans are triggered automatically when agents need healing. The SDK handles the two-step process:
- **Step 1**: Automatically triggers scan when performance is poor
- **Step 2**: Only attempts healing if agent is flagged (prevents 400 errors)

### Understanding Scan Results
- **Healthy Agents**: Performing well, no action needed
- **Warning Agents**: Minor issues detected, monitor closely
- **Critical Agents**: Significant problems, immediate attention required

## Step 3: Analyzing Agent Issues

### Common Performance Issues
- **Low Quality Scores**: Responses not meeting expectations
- **High Error Rates**: Frequent failures or incorrect responses
- **Slow Response Times**: Taking too long to respond
- **User Dissatisfaction**: Poor feedback scores
- **Inconsistent Responses**: Varying quality across interactions

### Root Cause Analysis
- **Prompt Issues**: Unclear or conflicting instructions
- **Context Problems**: Insufficient background information
- **Capability Gaps**: Agent trying to do things it's not designed for
- **Training Data Issues**: Poor examples or outdated information

## Step 4: Applying Healing Solutions

### Reviewing Recommendations
1. Examine the suggested prompt changes
2. Understand what each change will accomplish
3. Consider the potential impact on agent behavior
4. Test changes in a safe environment if possible

### Applying Changes
1. Select the agents you want to heal
2. Review the proposed changes
3. Apply changes to individual agents
4. Monitor the results after healing

### Healing Strategies
- **Prompt Refinement**: Clarify instructions and examples
- **Context Enhancement**: Add relevant background information
- **Constraint Adjustment**: Modify boundaries and limitations
- **Response Formatting**: Improve output structure and clarity

## Step 5: Error Prevention and SDK Integration

### Understanding the Two-Step Process
The self-healing system uses a robust two-step process to prevent errors:

1. **Performance Scan**: 
   - Detects when agents need healing (status: warning/critical)
   - Triggers a self-healing scan to flag underperforming agents
   - Validates that agents meet healing criteria before proceeding

2. **Prompt Improvement**:
   - Only flagged agents can be healed (prevents 400 errors)
   - Generates improved system prompts using Claude
   - Applies changes automatically or suggests improvements

### SDK Integration Benefits
When using the drop-in SDK, self-healing is completely automatic:

\`\`\`python
from enable_ai_sdk import create_monitored_agent

# Create monitored agent (3 lines!)
monitored_agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model
)

# Self-healing happens in the background:
# 1. Detects poor performance
# 2. Triggers scan to flag agent
# 3. Applies prompt improvements
# 4. No errors or manual intervention needed!
\`\`\`

### Error Prevention Features
- **No more 400 errors**: SDK handles all validation automatically
- **Smart detection**: Only heals agents that actually need improvement
- **Graceful fallback**: Continues working even if healing fails
- **Automatic retry**: Handles network issues and temporary failures

## Step 6: Monitoring Healing Results

### Post-Healing Analysis
- **Performance Tracking**: Monitor quality scores after changes
- **User Feedback**: Collect new feedback from users
- **Response Analysis**: Review actual agent responses
- **Trend Monitoring**: Track improvement over time

### Success Metrics
- **Quality Score Improvement**: Higher scores after healing
- **Error Rate Reduction**: Fewer failed interactions
- **User Satisfaction**: Better feedback scores
- **Response Consistency**: More predictable behavior

## Step 7: Advanced Healing Techniques

### Predictive Healing
- Monitor trends to predict when agents need attention
- Set up proactive healing schedules
- Use historical data to anticipate issues

### A/B Testing
- Test different healing approaches
- Compare performance between versions
- Choose the most effective solutions

### Batch Healing
- Apply similar fixes to multiple agents
- Standardize common improvements
- Maintain consistency across your agent fleet

## Best Practices

### Regular Maintenance
- Run healing scans weekly
- Review all flagged agents
- Apply improvements promptly
- Monitor results continuously

### Documentation
- Keep records of healing changes
- Document what worked and what didn't
- Share successful strategies across teams
- Build a knowledge base of effective fixes

### Team Collaboration
- Involve subject matter experts in healing decisions
- Get feedback from users before and after changes
- Share insights across your organization
- Learn from successful healing strategies

## Troubleshooting Common Issues

### Agents Not Improving
- Check if the root cause was correctly identified
- Verify that changes were applied correctly
- Consider if the agent type is appropriate for the use case
- Review if the prompt changes address the actual problem

### Over-Healing
- Be careful not to make too many changes at once
- Test changes before applying broadly
- Monitor for unintended consequences
- Roll back changes if necessary

### False Positives
- Verify that flagged issues are real problems
- Check if metrics are being interpreted correctly
- Consider if thresholds are set appropriately
- Review if the agent's purpose aligns with expectations
          `,
          tags: ['self-healing', 'monitoring', 'optimization', 'performance']
        },
        {
          title: 'Agent Analytics Dashboard - Master Guide',
          description: 'Comprehensive guide to understanding and using agent performance analytics',
          content: `
# Agent Analytics Dashboard - Master Guide

## Dashboard Overview

### What You'll Find Here
The Agent Analytics Dashboard provides comprehensive insights into your AI agents' performance, including response quality, usage patterns, improvement trends, and AI-generated recommendations.

### Key Metrics Explained
- **Quality Score**: Overall performance rating (1-10 scale)
- **Response Time**: Average time to respond to queries
- **Success Rate**: Percentage of successful interactions
- **User Satisfaction**: Feedback-based satisfaction scores
- **Usage Volume**: Number of interactions per time period

## Step 1: Navigating the Dashboard

### Main Dashboard Sections
1. **Overview Cards**: High-level performance metrics
2. **Agent List**: Individual agent performance summary
3. **Trend Charts**: Performance over time
4. **Detailed Analytics**: Deep-dive into specific metrics
5. **AI Insights**: Automated recommendations

### Filtering and Selection
- **Date Range**: Analyze specific time periods
- **Agent Selection**: Focus on individual agents
- **Tool Filtering**: Filter by specific agent tools
- **Use Case Filtering**: Analyze by use case type

## Step 2: Understanding Performance Metrics

### Quality Score Analysis
- **Excellent (9-10)**: Outstanding performance, no action needed
- **Good (7-8)**: Solid performance, minor optimizations possible
- **Average (5-6)**: Acceptable but room for improvement
- **Poor (1-4)**: Needs immediate attention and optimization

### Response Time Metrics
- **Fast (< 2 seconds)**: Excellent user experience
- **Moderate (2-5 seconds)**: Acceptable performance
- **Slow (> 5 seconds)**: May impact user satisfaction

### Success Rate Analysis
- **High (> 90%)**: Excellent reliability
- **Good (80-90%)**: Solid performance
- **Average (70-80%)**: Room for improvement
- **Low (< 70%)**: Needs immediate attention

## Step 3: Using AI-Generated Insights

### Getting Insights
1. Select the agents you want to analyze
2. Set your desired date range
3. Click "Get Insights" to generate AI recommendations
4. Review the detailed analysis and suggestions

### Understanding AI Recommendations
- **Performance Trends**: Whether agents are improving, steady, or declining
- **Issue Identification**: Specific problems affecting performance
- **Actionable Suggestions**: Concrete steps to improve performance
- **Priority Ranking**: Which issues to address first

### Acting on Insights
1. Review all recommendations carefully
2. Prioritize based on impact and effort
3. Implement changes systematically
4. Monitor results after implementation

## Step 4: Deep-Dive Analytics

### Performance Trends
- **Improving**: Positive trend, continue current strategies
- **Steady**: Stable performance, monitor for changes
- **Declining**: Negative trend, immediate action required

### Usage Pattern Analysis
- **Peak Usage Times**: When agents are most active
- **Popular Features**: Most-used agent capabilities
- **User Engagement**: How users interact with agents
- **Geographic Distribution**: Where agents are being used

### Comparative Analysis
- **Agent-to-Agent**: Compare performance between agents
- **Time Periods**: Compare current vs. historical performance
- **Use Cases**: Compare performance across different scenarios
- **User Segments**: Compare performance for different user types

## Step 5: Exporting and Reporting

### Data Export Options
- **CSV Export**: Download raw data for external analysis
- **Chart Exports**: Save visualizations for presentations
- **Custom Reports**: Generate specific reports for stakeholders
- **Scheduled Reports**: Set up automatic report generation

### Creating Reports for Stakeholders
1. **Executive Summary**: High-level performance overview
2. **Detailed Analysis**: Deep-dive into specific metrics
3. **Recommendations**: Actionable insights and next steps
4. **Trends**: Performance over time and predictions

## Step 6: Advanced Analytics Features

### Custom Dashboards
- Create personalized views for different roles
- Focus on specific metrics relevant to your needs
- Set up alerts for important thresholds
- Share dashboards with team members

### Predictive Analytics
- Forecast future performance based on trends
- Identify potential issues before they occur
- Plan capacity and resource allocation
- Set realistic performance targets

### Integration with Other Tools
- Connect with your existing analytics platforms
- Export data to business intelligence tools
- Integrate with customer feedback systems
- Connect with performance monitoring tools

## Best Practices

### Regular Review Schedule
- **Daily**: Check for critical issues
- **Weekly**: Review performance trends
- **Monthly**: Deep-dive analysis and planning
- **Quarterly**: Strategic review and goal setting

### Action Planning
- Set specific, measurable goals for improvement
- Create action plans based on insights
- Assign responsibilities for implementation
- Track progress toward goals

### Team Collaboration
- Share insights across your organization
- Involve stakeholders in decision-making
- Train team members on using the dashboard
- Create feedback loops for continuous improvement

## Troubleshooting Common Issues

### Missing Data
- Check if agents are properly configured
- Verify that monitoring is enabled
- Ensure API connections are working
- Review data retention settings

### Inconsistent Metrics
- Verify data collection methods
- Check for time zone issues
- Review aggregation methods
- Validate metric calculations

### Performance Issues
- Check system resources
- Review query optimization
- Verify data processing pipelines
- Monitor system health
          `,
          tags: ['analytics', 'performance', 'metrics', 'insights']
        },
        {
          title: 'Agent Feedback Dashboard - Complete Walkthrough',
          description: 'Master the feedback system to continuously improve your AI agents',
          content: `
# Agent Feedback Dashboard - Complete Walkthrough

## Understanding the Feedback System

### What is Agent Feedback?
Agent feedback is a systematic way to evaluate and improve your AI agents' performance through user evaluations, quality assessments, and detailed analysis of agent responses.

### Why Feedback Matters
- **Quality Assurance**: Ensures agents meet performance standards
- **Continuous Improvement**: Identifies areas for optimization
- **User Satisfaction**: Tracks how well agents serve user needs
- **Problem Detection**: Catches issues before they become widespread

## Step 1: Accessing the Feedback Dashboard

### Navigation
1. Go to "Agent Feedback Dashboard" in the console
2. Review the overview of recent feedback
3. Use filters to focus on specific agents or time periods
4. Explore detailed feedback entries

### Dashboard Overview
- **Recent Feedback**: Latest evaluations and assessments
- **Quality Distribution**: Spread of quality scores
- **Issue Categories**: Types of problems identified
- **Agent Performance**: Individual agent feedback summary

## Step 2: Understanding Feedback Metrics

### Quality Score Breakdown
- **10**: Exceptional performance, exceeds expectations
- **8-9**: Excellent performance, minor improvements possible
- **6-7**: Good performance, some room for improvement
- **4-5**: Average performance, significant improvements needed
- **1-3**: Poor performance, immediate attention required

### Issue Categories
- **Response Accuracy**: Incorrect or inaccurate information
- **Response Relevance**: Answers not relevant to the question
- **Response Completeness**: Missing important information
- **Response Clarity**: Unclear or confusing explanations
- **Response Tone**: Inappropriate communication style
- **Technical Issues**: System errors or malfunctions

## Step 3: Submitting New Feedback

### When to Submit Feedback
- After significant agent interactions
- When you notice performance issues
- Following agent updates or changes
- During regular quality reviews
- When users report problems

### How to Submit Quality Feedback
1. **Select the Agent**: Choose the specific agent being evaluated
2. **Choose the Tool**: Select the tool or feature being tested
3. **Provide Context**: Include the user's question or request
4. **Show the Response**: Paste the agent's actual response
5. **Rate the Quality**: Assign a quality score (1-10)
6. **Identify Issues**: Select the main problem category
7. **Add Details**: Provide specific feedback and suggestions

### Feedback Best Practices
- **Be Specific**: Include exact questions and responses
- **Be Objective**: Focus on facts rather than opinions
- **Be Constructive**: Provide actionable suggestions
- **Be Timely**: Submit feedback soon after interactions
- **Be Consistent**: Use the same evaluation criteria

## Step 4: Analyzing Feedback Data

### Filtering and Sorting
- **By Agent**: Focus on specific agents
- **By Tool**: Analyze specific features
- **By Quality Score**: Focus on high or low performers
- **By Issue Type**: Identify common problems
- **By Date Range**: Analyze trends over time

### Trend Analysis
- **Performance Trends**: Track quality scores over time
- **Issue Patterns**: Identify recurring problems
- **Improvement Tracking**: Monitor the impact of changes
- **User Satisfaction**: Track overall satisfaction trends

### Statistical Insights
- **Average Quality Scores**: Overall performance metrics
- **Response Time Analysis**: Speed vs. quality trade-offs
- **Issue Distribution**: Most common problems
- **Agent Comparison**: Performance across different agents

## Step 5: Using Feedback for Improvement

### Identifying Improvement Opportunities
1. **Review Low-Quality Feedback**: Focus on scores below 6
2. **Analyze Common Issues**: Look for patterns in problems
3. **Compare Agent Performance**: Identify best practices
4. **Track Improvement Trends**: Monitor the impact of changes

### Action Planning
- **Immediate Actions**: Address critical issues quickly
- **Short-term Improvements**: Make changes within days/weeks
- **Long-term Optimization**: Plan strategic improvements
- **Process Improvements**: Enhance feedback collection

### Implementation Strategies
- **Prompt Updates**: Modify agent instructions based on feedback
- **Training Data**: Add examples from successful interactions
- **System Improvements**: Fix technical issues identified
- **Process Changes**: Improve how agents handle specific scenarios

## Step 6: Advanced Feedback Features

### Automated Feedback Collection
- **User Feedback**: Collect ratings from actual users
- **System Monitoring**: Automatic quality assessments
- **Performance Alerts**: Notifications for quality drops
- **Trend Analysis**: Predictive quality monitoring

### Feedback Integration
- **CRM Integration**: Connect feedback with customer data
- **Support Ticket Integration**: Link feedback to support issues
- **Analytics Integration**: Combine with performance metrics
- **Reporting Integration**: Include in regular reports

### Feedback Workflows
- **Escalation Procedures**: When to involve human review
- **Quality Gates**: Minimum standards for deployment
- **Review Processes**: How to evaluate feedback systematically
- **Action Tracking**: Following up on improvement plans

## Best Practices

### Regular Feedback Reviews
- **Daily**: Check for critical issues
- **Weekly**: Review trends and patterns
- **Monthly**: Comprehensive analysis and planning
- **Quarterly**: Strategic review and goal setting

### Quality Standards
- Set minimum quality thresholds
- Define acceptable performance levels
- Establish escalation procedures
- Create improvement roadmaps

### Team Involvement
- Train team members on feedback submission
- Share insights across the organization
- Involve subject matter experts in reviews
- Create feedback-driven improvement culture

## Troubleshooting Common Issues

### Missing Feedback Data
- Check if feedback collection is enabled
- Verify agent configurations
- Review user access permissions
- Ensure proper data flow

### Inconsistent Feedback
- Standardize evaluation criteria
- Train reviewers on consistent scoring
- Use clear guidelines for quality assessment
- Regular calibration of feedback standards

### Feedback Quality Issues
- Provide training on effective feedback
- Use structured feedback forms
- Encourage detailed explanations
- Review and improve feedback processes
          `,
          tags: ['feedback', 'evaluation', 'quality', 'improvement']
        }
      ]
    },
    'webhooks-sdk': {
      title: 'Webhooks & SDK Integration',
      icon: <Code className="w-5 h-5" />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      items: [
        {
          title: 'Webhooks - Complete Integration Guide',
          description: 'Step-by-step guide to setting up and using webhooks for real-time notifications',
          content: `
# Webhooks - Complete Integration Guide

## Understanding Webhooks

### What Are Webhooks?
Webhooks are HTTP callbacks that send real-time data to your applications when specific events occur. They allow you to integrate Enable.AI with your existing systems and workflows without polling for updates.

### How Webhooks Work
1. **Event Occurs**: Something happens in Enable.AI (agent interaction, performance alert, etc.)
2. **HTTP POST**: Enable.AI sends a POST request to your webhook URL
3. **Your Server Responds**: Your application receives and processes the data
4. **Action Taken**: Your system performs actions based on the webhook data

## Step 1: Setting Up Webhooks

### Prerequisites
- A publicly accessible HTTPS endpoint
- Server capable of handling HTTP POST requests
- Proper authentication and security measures
- Error handling and retry logic

### Creating Your Webhook Endpoint
\`\`\`javascript
// Example Express.js webhook endpoint
app.post('/webhook/enable-ai', (req, res) => {
  const { event, data, timestamp } = req.body;
  
  // Verify webhook signature (recommended)
  if (!verifySignature(req)) {
    return res.status(401).send('Unauthorized');
  }
  
  // Process the webhook data
  switch (event) {
    case 'agent.interaction':
      handleAgentInteraction(data);
      break;
    case 'agent.performance_alert':
      handlePerformanceAlert(data);
      break;
    case 'agent.self_healing':
      handleSelfHealing(data);
      break;
    default:
      console.log('Unknown event:', event);
  }
  
  res.status(200).send('OK');
});
\`\`\`

### Webhook Security
- **Signature Verification**: Always verify webhook signatures
- **HTTPS Only**: Never use HTTP for webhooks
- **Rate Limiting**: Implement rate limiting on your endpoint
- **Error Handling**: Return appropriate HTTP status codes

## Step 2: Configuring Webhooks in Enable.AI

### Accessing Webhook Settings
1. Navigate to "Webhook Management" in the console
2. Click "Create New Webhook"
3. Configure the webhook settings

### Required Configuration
- **Webhook URL**: Your endpoint URL (must be HTTPS)
- **Event Types**: Which events to receive
- **Authentication**: Optional headers for security
- **Retry Settings**: How to handle failed deliveries

### Event Types Available
- **agent.interaction**: When agents interact with users
- **agent.performance_alert**: When performance drops below thresholds
- **agent.self_healing**: When self-healing actions are taken
- **agent.feedback**: When new feedback is submitted
- **system.maintenance**: System updates and maintenance

## Step 3: Webhook Payload Structure

### Standard Payload Format
\`\`\`json
{
  "event": "agent.interaction",
  "timestamp": "2024-01-01T12:00:00Z",
  "data": {
    "agent_id": "agent_123",
    "agent_name": "Customer Service Bot",
    "user_id": "user_456",
    "interaction_id": "int_789",
    "query": "How do I reset my password?",
    "response": "To reset your password...",
    "quality_score": 8.5,
    "response_time": 2.3
  },
  "signature": "sha256=abc123..."
}
\`\`\`

### Event-Specific Data
Each event type includes relevant data:
- **Interaction Events**: Query, response, quality metrics
- **Performance Events**: Quality scores, error rates, trends
- **Healing Events**: Issues identified, changes applied
- **Feedback Events**: User feedback, quality scores

## Step 4: Handling Webhook Events

### Agent Interaction Events
\`\`\`javascript
function handleAgentInteraction(data) {
  // Log the interaction
  console.log(\`Agent \${data.agent_name} responded to user query\`);
  
  // Store in database
  await db.interactions.create({
    agentId: data.agent_id,
    userId: data.user_id,
    query: data.query,
    response: data.response,
    qualityScore: data.quality_score,
    timestamp: new Date()
  });
  
  // Trigger follow-up actions
  if (data.quality_score < 6) {
    await notifyTeam('Low quality response detected');
  }
}
\`\`\`

### Performance Alert Events
\`\`\`javascript
function handlePerformanceAlert(data) {
  // Alert the team
  await sendSlackNotification({
    channel: '#ai-monitoring',
    text: \`Performance alert for \${data.agent_name}: \${data.issue}\`
  });
  
  // Create support ticket
  await createTicket({
    title: \`AI Agent Performance Issue\`,
    description: \`Agent \${data.agent_name} has performance issues\`,
    priority: 'high'
  });
}
\`\`\`

### Self-Healing Events
\`\`\`javascript
function handleSelfHealing(data) {
  // Log the healing action
  console.log(\`Agent \${data.agent_name} was healed: \${data.changes}\`);
  
  // Update monitoring dashboard
  await updateDashboard({
    agentId: data.agent_id,
    status: 'healed',
    changes: data.changes
  });
  
  // Notify stakeholders
  await sendEmail({
    to: 'ai-team@company.com',
    subject: \`Agent \${data.agent_name} Auto-Healed\`,
    body: \`The agent was automatically optimized.\`
  });
}
\`\`\`

## Step 5: Testing and Debugging

### Testing Your Webhook
1. **Use Webhook Testing Tools**: Tools like webhook.site for testing
2. **Local Development**: Use ngrok for local testing
3. **Logging**: Implement comprehensive logging
4. **Monitoring**: Set up alerts for webhook failures

### Common Issues and Solutions
- **404 Errors**: Check your webhook URL is correct
- **Timeout Errors**: Ensure your endpoint responds quickly
- **Authentication Errors**: Verify signature verification
- **Payload Issues**: Check JSON parsing and data structure

### Debugging Checklist
- [ ] Webhook URL is accessible and responding
- [ ] HTTPS is properly configured
- [ ] Signature verification is working
- [ ] Error handling is implemented
- [ ] Logging is comprehensive
- [ ] Monitoring is set up

## Step 6: Production Best Practices

### Reliability
- **Idempotency**: Handle duplicate webhooks gracefully
- **Retry Logic**: Implement exponential backoff
- **Dead Letter Queues**: Store failed webhooks for later processing
- **Monitoring**: Track webhook delivery success rates

### Security
- **Signature Verification**: Always verify webhook signatures
- **HTTPS Only**: Never accept HTTP webhooks
- **Rate Limiting**: Protect your endpoint from abuse
- **Input Validation**: Validate all incoming data

### Performance
- **Async Processing**: Process webhooks asynchronously
- **Database Optimization**: Use efficient database operations
- **Caching**: Cache frequently accessed data
- **Load Balancing**: Distribute webhook processing

## Troubleshooting Guide

### Webhook Not Receiving Events
1. Check webhook URL is correct and accessible
2. Verify HTTPS is properly configured
3. Check firewall and network settings
4. Review Enable.AI webhook configuration

### Authentication Issues
1. Verify webhook signature verification
2. Check authentication headers
3. Review security settings
4. Test with webhook testing tools

### Performance Issues
1. Optimize webhook processing
2. Implement async processing
3. Add caching where appropriate
4. Monitor response times
          `,
          tags: ['webhooks', 'integration', 'real-time', 'notifications']
        },
        {
          title: 'Drop-in Agent Monitoring SDK - Complete Guide',
          description: 'Comprehensive guide to automatically monitoring and improving your AI agents',
          content: `
# Drop-in Agent Monitoring SDK - Complete Guide

## Understanding the Drop-in SDK

### What is the Drop-in SDK?
The EnableAI Drop-in SDK provides automatic performance monitoring and self-healing for any AI agent. Simply wrap your existing agent and get automatic quality evaluation, performance tracking, and prompt improvements.

### SDK Benefits
- **Drop-in Integration**: Just import and wrap your agent
- **Automatic Monitoring**: Every interaction gets evaluated
- **Self-Healing**: Automatic improvements when needed
- **Real-time Insights**: Performance analytics and recommendations
- **Zero Manual Work**: Everything happens automatically

## Step 1: Installing the Drop-in SDK

### Python Installation
\`\`\`bash
# Install the drop-in SDK
pip install enable-ai-sdk

# Or install from local directory (development)
pip install -e .
\`\`\`

### Package Structure
The drop-in SDK includes:
\`\`\`
enable_ai_sdk/
├── agent_monitor.py     # Drop-in monitoring wrapper
├── __init__.py          # Main package exports
└── examples/            # Integration examples
\`\`\`

### Dependencies
The SDK requires:
- **Python**: 3.7 or higher
- **requests**: 2.25.0 or higher

## Step 2: Quick Integration

### Simple Integration (3 lines!)
\`\`\`python
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Your existing AI model function
def your_ai_model(prompt: str) -> str:
    return "Response from your AI model"

# Create monitored agent (3 lines!)
monitored_agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model
)

# Use it normally - everything is automatic!
response = monitored_agent.generate_response("What is your return policy?")
\`\`\`

## Step 3: What Happens Automatically

### Every Interaction
1. **Your agent generates a response**
2. **SDK automatically reports performance**
3. **Claude evaluates quality (1-100 score)**
4. **Performance is tracked and analyzed**
5. **Self-healing triggers if needed**
6. **Prompt improvements are applied automatically**

### Advanced Integration with Real AI Models
\`\`\`python
from enable_ai_sdk.agent_monitor import AgentMonitor
import openai

class MonitoredCustomerSupportAgent(AgentMonitor):
    def _call_ai_model(self, prompt: str, **kwargs) -> str:
        # Your AI model integration here
        client = openai.OpenAI(api_key=os.environ["OPENAI_API_KEY"])
        response = client.chat.completions.create(
            model="gpt-4",
            messages=[
                {"role": "system", "content": self.system_prompt},
                {"role": "user", "content": prompt}
            ]
        )
        return response.choices[0].message.content

# Create the agent
agent = MonitoredCustomerSupportAgent(
    agent_id="your-agent-id",
    api_key="your-api-key"
)

# Use it - everything is automatic!
response = agent.generate_response("What is your return policy?")
\`\`\`

### AWS Lambda Integration
\`\`\`python
import json
from enable_ai_sdk.agent_monitor import create_monitored_agent

# Your AI model function
def my_ai_model(prompt: str) -> str:
    return "Response from your AI model"

# Create monitored agent
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=my_ai_model
)

def lambda_handler(event, context):
    user_input = event['body']['message']
    response = agent.generate_response(user_input)  # Automatically monitored!
    return {'response': response}
\`\`\`

### Docker Container Integration
\`\`\`python
# In your Docker container
from enable_ai_sdk.agent_monitor import create_monitored_agent
import openai

def my_openai_agent(prompt: str) -> str:
    client = openai.OpenAI(api_key=os.environ["OPENAI_API_KEY"])
    response = client.chat.completions.create(
        model="gpt-4",
        messages=[
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": prompt}
        ]
    )
    return response.choices[0].message.content

# Create monitored agent
agent = create_monitored_agent(
    agent_id=os.environ["AGENT_ID"],
    api_key=os.environ["ENABLEAI_API_KEY"],
    ai_model_func=my_openai_agent
)

# Your agent is now automatically monitored!
response = agent.generate_response("What is your return policy?")
\`\`\`

## 🎯 Key Benefits

### For Agent Developers
- **Drop-in Integration**: Just import and wrap your agent
- **Automatic Monitoring**: Every interaction gets evaluated
- **Self-Healing**: Automatic improvements when needed
- **Real-time Insights**: Performance analytics and recommendations

### For Agent Operators
- **Zero Manual Work**: Everything is automatic
- **Proactive Alerts**: Get notified when agents need attention
- **Continuous Improvement**: Agents get better over time
- **Comprehensive Analytics**: Detailed performance tracking

## 📊 What Gets Tracked Automatically

### Performance Metrics
- **Quality Score**: 1-100 rating from Claude
- **Response Time**: How long your agent takes
- **Issue Categories**: Hallucination, tone, format, etc.
- **Usage Patterns**: When and how your agent is used

### Self-Healing Triggers
- **Poor Performance**: Average score < 75
- **Critical Issues**: Average score < 60
- **Declining Trends**: Performance getting worse
- **Specific Issues**: Hallucination, tone problems, etc.

### Automatic Actions
- **Prompt Improvements**: AI-generated better prompts
- **Health Monitoring**: Real-time status checking
- **Performance Reporting**: Every interaction evaluated
- **Insight Generation**: AI recommendations

## 🔧 Configuration Options

### Basic Setup
\`\`\`python
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model
)
\`\`\`

### Advanced Configuration
\`\`\`python
agent = create_monitored_agent(
    agent_id="your-agent-id",
    api_key="your-api-key",
    ai_model_func=your_ai_model,
    base_url="https://your-backend.com",
    auto_healing=True,      # Enable automatic prompt improvements
    report_async=True       # Report performance asynchronously
)
\`\`\`

## 📈 Monitoring and Analytics

### Get Health Status
\`\`\`python
health = agent.get_health_status()
print(f"Status: {health['status']}, Score: {health['average_score']}")
\`\`\`

### Get Analytics
\`\`\`python
analytics = agent.get_analytics()
print(f"Total interactions: {analytics['total_logs']}")
\`\`\`

### Get AI Insights
\`\`\`python
insights = agent.get_insights()
print(f"Trend: {insights['score_trend']}")
print(f"Actions: {insights['suggested_actions']}")
\`\`\`
    llm="claude-3-5-sonnet-20241022",
    description="Handles customer inquiries and support requests",
    system_prompt="You are a helpful customer service representative..."
)

print(f"Agent created: {agent.id}")
\`\`\`

### Listing Agents
\`\`\`python
# List all agents
agents = client.agents.list()
for agent in agents:
    print(f"- {agent.name} ({agent.agent_type})")
\`\`\`

### Getting Agent Information
\`\`\`python
# Get agent details (from list)
agents = client.agents.list()
for agent in agents:
    print(f"Agent name: {agent.name}")
    print(f"Agent type: {agent.agent_type}")
    print(f"LLM: {agent.llm}")
\`\`\`

## Step 4: Advanced Agent Management

### Updating Agent Prompts
\`\`\`python
# Update agent (requires user session auth)
updated_agent = client.agents.update(
    agent_id=agent.id,
    name="Updated Agent Name",
    description="Updated description"
)

# Get prompt history
history = client.agents.get_prompt_history(agent.id)
for change in history:
    print(f"Changed on {change.get('timestamp')}: {change.get('description')}")
\`\`\`

### Agent Performance Analytics
\`\`\`python
# Get agent insights
insights = client.analytics.get_agent_insights(
    agent_id=agent.id,
    start_date="2024-01-01",
    end_date="2024-01-31"
)

print(f"Agent: {insights.agent_name}")
print(f"Score Trend: {insights.score_trend}")
print(f"Average Score: {insights.average_score}")
print(f"Feedback Count: {insights.feedback_count}")
\`\`\`

### Self-Healing Operations
\`\`\`python
# Run self-healing scan
scan_results = client.self_healing.scan()
print(f"Agents scanned: {scan_results['total_agents_scanned']}")
print(f"Agents flagged: {len(scan_results['agents_flagged'])}")

# Heal a specific agent
healing_result = client.self_healing.heal_agent(
    agent_id=agent.id,
    strategy="auto"
)
\`\`\`

## Step 5: Feedback and Evaluation

### Submitting Feedback
\`\`\`python
# Submit feedback for evaluation
feedback = client.analytics.submit_feedback(
    prompt="What is your return policy?",
    response="Our return policy allows returns within 30 days.",
    tool="CustomerFeedback",
    use_case="Customer Support",
    agent_id=agent.id
)

print(f"Feedback Score: {feedback.score}")
print(f"Issue: {feedback.issue}")
\`\`\`

### Getting Feedback Analytics
\`\`\`python
# Get detailed agent analytics
analytics = client.analytics.get_agent_analytics(
    agent_id=agent.id,
    start_date="2024-01-01",
    end_date="2024-01-31",
    tool="CustomerFeedback",
    use_case="Customer Support"
)

print(f"Total feedback submissions: {analytics.get('total_submissions', 0)}")
print(f"Average quality score: {analytics.get('average_score', 0)}")
print(f"Recent issues: {analytics.get('recent_issues', [])}")
\`\`\`

## Step 6: Webhook Integration

### Setting Up Webhooks
\`\`\`python
# Create a webhook
webhook = client.webhooks.create(
    name="Agent Monitoring",
    url="https://your-app.com/webhook",
    events=["agent_self_healing_triggered", "feedback_submitted"],
    headers={"Authorization": "Bearer token"},
    retry_count=3,
    timeout=10,
    is_active=True
)

print(f"Webhook created: {webhook['id']}")
\`\`\`

### Verifying Webhook Signatures
\`\`\`javascript
import crypto from 'crypto';

function verifyWebhookSignature(payload, signature, secret) {
  const expectedSignature = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(\`sha256=\${expectedSignature}\`)
  );
}

// In your webhook endpoint
app.post('/webhook', (req, res) => {
  const signature = req.headers['x-enable-ai-signature'];
  const payload = JSON.stringify(req.body);
  
  if (!verifyWebhookSignature(payload, signature, 'your-webhook-secret')) {
    return res.status(401).send('Invalid signature');
  }
  
  // Process webhook
  console.log('Webhook received:', req.body);
  res.status(200).send('OK');
});
\`\`\`

## Step 7: Error Handling and Best Practices

### Error Handling
\`\`\`python
from enable_ai_sdk import EnableAIClient, AuthenticationError, ValidationError, RateLimitError

try:
    agent = client.agents.register(
        name="Test Agent",
        agent_type="customer-support",
        llm="claude-3-5-sonnet-20241022"
    )
except AuthenticationError as e:
    print(f"Authentication failed: {e}")
except ValidationError as e:
    print(f"Validation error: {e}")
except RateLimitError as e:
    print(f"Rate limit exceeded: {e}")
except Exception as e:
    print(f"Unexpected error: {e}")
\`\`\`

### Retry Logic
\`\`\`python
import time
from enable_ai_sdk import EnableAIClient, RateLimitError

def api_call_with_retry(client, func, *args, **kwargs):
    max_retries = 3
    retry_delay = 1
    
    for attempt in range(max_retries):
        try:
            return func(*args, **kwargs)
        except RateLimitError:
            if attempt < max_retries - 1:
                time.sleep(retry_delay * (2 ** attempt))  # Exponential backoff
                continue
            raise
        except Exception as e:
            raise e

# Usage
agent = api_call_with_retry(client, client.agents.register, "Test Agent", "customer-support", "claude-3-5-sonnet-20241022")
\`\`\`

### Logging and Monitoring
\`\`\`javascript
// Enable SDK logging
enableAI.setLogger({
  level: 'info',
  format: 'json'
});

// Custom event handlers
enableAI.on('request', (request) => {
  console.log('API Request:', request);
});

enableAI.on('response', (response) => {
  console.log('API Response:', response);
});

enableAI.on('error', (error) => {
  console.error('SDK Error:', error);
});
\`\`\`

## Step 8: Production Deployment

### Environment Configuration
\`\`\`python
import os
from enable_ai_sdk import EnableAIClient

# Production configuration
client = EnableAIClient(
    api_key=os.getenv('ENABLE_AI_API_KEY'),
    base_url=os.getenv('ENABLE_AI_BASE_URL', 'https://api.weenable.ai')
)
\`\`\`

### Security Best Practices
- Store API keys in environment variables
- Use HTTPS for all communications
- Implement proper error handling
- Monitor API usage and costs
- Rotate API keys regularly

### Performance Optimization
- Use connection pooling
- Implement caching where appropriate
- Monitor response times
- Set appropriate timeouts
- Use async/await properly

## Troubleshooting Guide

### Common Issues
1. **Authentication Errors**: Check API key and permissions
2. **Rate Limiting**: Implement exponential backoff
3. **Network Issues**: Check connectivity and timeouts
4. **SDK Version**: Ensure you're using the latest version

### Testing the Installation
\`\`\`python
from deckgen_sdk import DeckGenClient

# Test basic import
client = EnableAIClient(api_key="test-key", base_url="http://localhost:5001")
print("✅ SDK imported successfully!")

# Test health check (if backend is running)
try:
    health = client.health_check()
    print(f"✅ Backend health: {health}")
except Exception as e:
    print(f"ℹ️  Backend not available: {e}")
\`\`\`

### Quick Functions
For simple operations, you can use the convenience functions:

\`\`\`python
from enable_ai_sdk import quick_agent_register, quick_feedback_submit

# Quick agent registration
agent = quick_agent_register(
    api_key="your-key",
    name="Quick Agent",
    agent_type="customer-support"
)

# Quick feedback submission
feedback = quick_feedback_submit(
    api_key="your-key",
    prompt="What is your return policy?",
    response="Our return policy allows returns within 30 days.",
    tool="CustomerFeedback",
    use_case="Customer Support"
)
\`\`\`

## Integration Examples

### Flask Integration
\`\`\`python
from flask import Flask, request, jsonify
from enable_ai_sdk import EnableAIClient

app = Flask(__name__)
client = EnableAIClient(api_key=os.getenv('ENABLE_AI_API_KEY'))

@app.route('/register_agent', methods=['POST'])
def register_agent():
    data = request.json
    agent = client.agents.register(
        name=data['name'],
        agent_type=data['agent_type'],
        llm=data['llm']
    )
    return jsonify({
        'agent_id': agent.id,
        'name': agent.name,
        'status': 'registered'
    })
\`\`\`

### Django Integration
\`\`\`python
from django.http import JsonResponse
from enable_ai_sdk import EnableAIClient

client = EnableAIClient(api_key=os.getenv('ENABLE_AI_API_KEY'))

def register_agent(request):
    if request.method == 'POST':
        data = json.loads(request.body)
        agent = client.agents.register(
            name=data['name'],
            agent_type=data['agent_type'],
            llm=data['llm']
        )
        return JsonResponse({
            'agent_id': agent.id,
            'name': agent.name
        })
\`\`\`

### Getting Help
- Check the SDK documentation
- Review error messages carefully
- Test with the SDK examples
- Contact EnableAI support
          `,
          tags: ['sdk', 'integration', 'api', 'development']
        }
      ]
    },
    'real-world-walkthrough': {
      title: 'Real-World Use Case Walkthrough',
      icon: <Play className="w-5 h-5" />,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      items: [
        {
          title: 'Customer Support AI Agent - Complete Implementation',
          description: 'Step-by-step walkthrough of building and optimizing a customer support AI agent',
          content: `
# Customer Support AI Agent - Complete Implementation

## Use Case Overview

### The Challenge
TechCorp, a SaaS company, receives 500+ customer support tickets daily. Their support team is overwhelmed, and response times are increasing. They need an AI agent that can:
- Handle common customer inquiries
- Provide accurate technical support
- Escalate complex issues to humans
- Learn and improve over time

### The Solution
We'll build a comprehensive customer support AI agent using Enable.AI's full tooling suite, from initial creation to production deployment and continuous optimization.

## Phase 1: Planning and Setup

### Step 1: Define Requirements
**Business Requirements:**
- Handle 80% of common support inquiries automatically
- Maintain response quality score above 8.0
- Escalate complex issues within 2 minutes
- Support multiple product lines and technical topics

**Technical Requirements:**
- Integration with existing CRM system
- Real-time monitoring and alerting
- Automatic performance optimization
- Comprehensive feedback collection

### Step 2: Prepare Your Environment
1. **Access Enable.AI Console**: Navigate to the console and ensure you have admin access
2. **Set Up API Keys**: Generate and configure API keys for integration
3. **Prepare Knowledge Base**: Gather product documentation, FAQs, and support materials
4. **Define Success Metrics**: Establish KPIs for quality, speed, and satisfaction

## Phase 2: Agent Creation and Configuration

### Step 3: Create the Customer Support Agent

**Navigate to Agent Manager:**
1. Go to "Manage My Agents" in the console
2. Click "Register New Agent"
3. Fill in the agent details:

\`\`\`
Agent Name: TechCorp Customer Support Bot
Agent Type: customer_service
Description: Handles customer inquiries for TechCorp SaaS products, provides technical support, and escalates complex issues to human agents.
\`\`\`

### Step 4: Craft the Initial System Prompt

**Access the Prompt Editor:**
1. Go to "Edit Agent Prompts"
2. Select your newly created agent
3. Implement this comprehensive system prompt:

\`\`\`
You are TechCorp's Customer Support AI Assistant. Your role is to provide excellent customer service and technical support for our SaaS products.

**Your Capabilities:**
- Answer questions about TechCorp products and services
- Provide step-by-step troubleshooting guidance
- Help with account management and billing questions
- Guide users through product features and functionality
- Escalate complex issues to human agents when necessary

**Your Knowledge Base:**
- Product documentation and user guides
- Common troubleshooting procedures
- Billing and account management policies
- Feature explanations and tutorials

**Response Guidelines:**
- Always be helpful, professional, and empathetic
- Provide clear, actionable solutions
- Use step-by-step instructions when appropriate
- Ask clarifying questions when needed
- Acknowledge customer frustration and show understanding
- Offer to escalate if you cannot fully resolve the issue

**Escalation Criteria:**
- Complex technical issues beyond basic troubleshooting
- Billing disputes or account access problems
- Feature requests or product suggestions
- Complaints about service quality
- Issues requiring account modifications

**Response Format:**
1. Acknowledge the customer's issue
2. Provide a clear, helpful response
3. Include relevant steps or instructions
4. Offer additional help or escalation if needed
5. End with a professional closing

**Quality Standards:**
- Maintain accuracy in all technical information
- Provide complete, helpful responses
- Show empathy and understanding
- Follow up appropriately when escalation is needed
\`\`\`

## Phase 3: Testing and Initial Deployment

### Step 5: Test the Agent

**Create Test Scenarios:**
1. **Basic Product Questions**: "How do I reset my password?"
2. **Technical Issues**: "My dashboard isn't loading properly"
3. **Billing Questions**: "When does my subscription renew?"
4. **Feature Requests**: "Can you add dark mode to the app?"
5. **Complex Issues**: "I need to integrate with our ERP system"

**Evaluate Responses:**
- Check accuracy and completeness
- Assess tone and professionalism
- Verify escalation triggers
- Test response time and quality

### Step 6: Set Up Monitoring

**Configure Analytics Dashboard:**
1. Navigate to "Agent Performance Dashboard"
2. Select your customer support agent
3. Set up monitoring for:
   - Quality scores (target: >8.0)
   - Response times (target: <30 seconds)
   - Escalation rates (target: <20%)
   - User satisfaction scores

**Set Up Alerts:**
- Quality score drops below 7.0
- Response time exceeds 60 seconds
- Escalation rate increases above 25%
- User satisfaction drops below 7.5

## Phase 4: Feedback Collection and Optimization

### Step 7: Implement Feedback System

**Set Up Feedback Collection:**
1. Go to "Agent Feedback Dashboard"
2. Configure automatic feedback collection
3. Set up manual feedback submission for users
4. Create feedback categories:
   - Response accuracy
   - Helpfulness
   - Professionalism
   - Escalation appropriateness

**Collect Initial Feedback:**
- Monitor first 100 interactions
- Gather feedback from support team
- Analyze common issues and patterns
- Identify improvement opportunities

### Step 8: Analyze and Optimize

**Review Performance Data:**
1. Check analytics dashboard for trends
2. Identify low-quality responses
3. Analyze common failure points
4. Review user feedback patterns

**Common Issues to Address:**
- **Incomplete Responses**: Add more detailed instructions
- **Wrong Escalations**: Refine escalation criteria
- **Poor Tone**: Adjust response guidelines
- **Technical Inaccuracies**: Update knowledge base

## Phase 5: Self-Healing and Continuous Improvement

### Step 9: Enable Self-Healing

**Configure Self-Healing:**
1. Navigate to "Agent Self-Healing"
2. Set up automatic monitoring
3. Configure healing thresholds:
   - Quality score < 7.5 triggers healing
   - Response time > 45 seconds triggers optimization
   - Escalation rate > 25% triggers review

**Run Initial Healing Scan:**
1. Click "Run Healing Scan"
2. Review identified issues
3. Apply suggested improvements
4. Monitor results over 24-48 hours

### Step 10: Monitor and Iterate

**Daily Monitoring:**
- Check quality scores and trends
- Review escalated cases
- Monitor user feedback
- Track performance metrics

**Weekly Optimization:**
- Analyze weekly performance reports
- Update system prompts based on feedback
- Refine escalation criteria
- Add new knowledge base content

**Monthly Review:**
- Comprehensive performance analysis
- Update agent capabilities
- Plan new features and improvements
- Review and adjust success metrics

## Phase 6: Production Deployment

### Step 11: Integration Setup

**Connect to Existing Systems:**
1. **CRM Integration**: Set up webhooks to sync with Salesforce
2. **Ticket System**: Integrate with Zendesk for escalations
3. **Knowledge Base**: Connect to internal documentation
4. **Analytics**: Set up reporting dashboards

**Webhook Configuration:**
\`\`\`javascript
// Example webhook for CRM integration
app.post('/webhook/customer-support', (req, res) => {
  const { event, data } = req.body;
  
  switch (event) {
    case 'agent.interaction':
      // Log interaction in CRM
      await salesforce.createCase({
        subject: \`AI Support: \${data.query.substring(0, 50)}\`,
        description: data.response,
        priority: data.quality_score < 7 ? 'High' : 'Medium'
      });
      break;
      
    case 'agent.escalation':
      // Create support ticket
      await zendesk.createTicket({
        subject: \`Escalated: \${data.reason}\`,
        description: data.interaction_history,
        priority: 'High'
      });
      break;
  }
  
  res.status(200).send('OK');
});
\`\`\`

### Step 12: Launch and Monitor

**Go-Live Checklist:**
- [ ] Agent tested with real scenarios
- [ ] Monitoring and alerts configured
- [ ] Feedback collection active
- [ ] Self-healing enabled
- [ ] Integration systems connected
- [ ] Support team trained on escalation process
- [ ] Success metrics defined and tracked

**Launch Day:**
1. Start with limited traffic (10% of support volume)
2. Monitor quality scores and response times
3. Watch for any issues or escalations
4. Gradually increase traffic over 1-2 weeks
5. Full deployment after successful pilot

## Phase 7: Results and Optimization

### Expected Outcomes

**After 30 Days:**
- **Response Quality**: Average score of 8.5+
- **Response Time**: Under 30 seconds average
- **Escalation Rate**: 15-20% of interactions
- **User Satisfaction**: 8.0+ rating
- **Cost Savings**: 60-70% reduction in support ticket volume

**After 90 Days:**
- **Self-Healing**: Agent automatically optimizes 80% of issues
- **Knowledge Growth**: Agent handles 85% of common inquiries
- **Team Efficiency**: Support team focuses on complex cases
- **Customer Satisfaction**: Improved overall support experience

### Continuous Improvement

**Monthly Optimization Cycle:**
1. **Week 1**: Analyze performance data and feedback
2. **Week 2**: Implement prompt improvements and new features
3. **Week 3**: Test changes and monitor results
4. **Week 4**: Review outcomes and plan next cycle

**Long-term Success Metrics:**
- 90%+ customer satisfaction with AI interactions
- 80%+ resolution rate without human escalation
- 50%+ reduction in support costs
- Improved support team productivity and job satisfaction

## Troubleshooting Common Issues

### Agent Not Responding Appropriately
1. **Check System Prompt**: Ensure guidelines are clear and comprehensive
2. **Review Knowledge Base**: Verify information is accurate and up-to-date
3. **Analyze Feedback**: Look for patterns in negative feedback
4. **Test Scenarios**: Run through common use cases manually

### High Escalation Rate
1. **Refine Escalation Criteria**: Make them more specific
2. **Improve Knowledge Base**: Add more comprehensive information
3. **Update System Prompt**: Provide better guidance for complex issues
4. **Train Support Team**: Ensure they understand escalation process

### Poor Quality Scores
1. **Review Low-Score Responses**: Identify common patterns
2. **Update System Prompt**: Address specific issues
3. **Add Training Examples**: Include more specific scenarios
4. **Enable Self-Healing**: Let the system optimize automatically

### Integration Issues
1. **Check Webhook Configuration**: Verify URLs and authentication
2. **Test API Connections**: Ensure all systems are communicating
3. **Review Error Logs**: Identify specific failure points
4. **Update Integration Code**: Fix any bugs or issues

## Best Practices Summary

### Agent Development
- Start with a comprehensive system prompt
- Test thoroughly before deployment
- Monitor performance continuously
- Iterate based on feedback and data

### Integration
- Use webhooks for real-time updates
- Implement proper error handling
- Set up comprehensive monitoring
- Plan for scalability from the start

### Optimization
- Enable self-healing for automatic improvements
- Collect and analyze feedback regularly
- Update knowledge base continuously
- Monitor and adjust success metrics

### Team Collaboration
- Train support team on escalation process
- Share insights and improvements across teams
- Document processes and procedures
- Celebrate successes and learn from failures
          `,
          tags: ['real-world', 'customer-support', 'implementation', 'optimization']
        }
      ]
    }
  };

  // Filter documentation based on search query
  const filteredDocs = Object.entries(documentation).reduce((acc, [categoryKey, category]) => {
    const filteredItems = category.items.filter(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    if (filteredItems.length > 0) {
      acc[categoryKey] = {
        ...category,
        items: filteredItems
      };
    }
    
    return acc;
  }, {});

  const toggleCategory = (categoryKey) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentListItems = [];
    let listType = null;

    lines.forEach((line, index) => {
      // Handle headers
      if (line.startsWith('# ')) {
        // Close any open list
        if (currentListItems.length > 0) {
          if (listType === 'ol') {
            elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
          } else if (listType === 'ul') {
            elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
          }
          currentListItems = [];
          listType = null;
        }
        elements.push(<h1 key={index} className="text-2xl font-bold text-gray-900 mb-4">{line.substring(2)}</h1>);
      } else if (line.startsWith('## ')) {
        if (currentListItems.length > 0) {
          if (listType === 'ol') {
            elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
          } else if (listType === 'ul') {
            elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
          }
          currentListItems = [];
          listType = null;
        }
        elements.push(<h2 key={index} className="text-xl font-semibold text-gray-800 mb-3 mt-6">{line.substring(3)}</h2>);
      } else if (line.startsWith('### ')) {
        if (currentListItems.length > 0) {
          if (listType === 'ol') {
            elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
          } else if (listType === 'ul') {
            elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
          }
          currentListItems = [];
          listType = null;
        }
        elements.push(<h3 key={index} className="text-lg font-medium text-gray-700 mb-2 mt-4">{line.substring(4)}</h3>);
      } 
      // Handle numbered lists
      else if (/^\d+\.\s/.test(line)) {
        const text = line.replace(/^\d+\.\s/, '');
        if (listType !== 'ol') {
          // Close previous list if different type
          if (currentListItems.length > 0 && listType !== null) {
            if (listType === 'ul') {
              elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
            }
            currentListItems = [];
          }
          listType = 'ol';
        }
        
        // Parse bold text in list items
        const boldRegex = /\*\*([^*]+)\*\*/g;
        const textElements = [];
        let lastIndex = 0;
        let match;
        
        while ((match = boldRegex.exec(text)) !== null) {
          // Add text before the bold part
          if (match.index > lastIndex) {
            textElements.push(text.slice(lastIndex, match.index));
          }
          // Add the bold text
          textElements.push(<strong key={`bold-${match.index}`} className="font-semibold text-gray-800">{match[1]}</strong>);
          lastIndex = match.index + match[0].length;
        }
        
        // Add any remaining text
        if (lastIndex < text.length) {
          textElements.push(text.slice(lastIndex));
        }
        
        currentListItems.push(<li key={index} className="text-gray-600">{textElements}</li>);
      }
      // Handle bullet points
      else if (line.startsWith('- ')) {
        const text = line.substring(2);
        if (listType !== 'ul') {
          // Close previous list if different type
          if (currentListItems.length > 0 && listType !== null) {
            if (listType === 'ol') {
              elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
            }
            currentListItems = [];
          }
          listType = 'ul';
        }
        
        // Parse bold text in list items
        const boldRegex = /\*\*([^*]+)\*\*/g;
        const textElements = [];
        let lastIndex = 0;
        let match;
        
        while ((match = boldRegex.exec(text)) !== null) {
          // Add text before the bold part
          if (match.index > lastIndex) {
            textElements.push(text.slice(lastIndex, match.index));
          }
          // Add the bold text
          textElements.push(<strong key={`bold-${match.index}`} className="font-semibold text-gray-800">{match[1]}</strong>);
          lastIndex = match.index + match[0].length;
        }
        
        // Add any remaining text
        if (lastIndex < text.length) {
          textElements.push(text.slice(lastIndex));
        }
        
        currentListItems.push(<li key={index} className="text-gray-600">{textElements}</li>);
      } 
      // Handle code blocks
      else if (line.startsWith('```')) {
        if (currentListItems.length > 0) {
          if (listType === 'ol') {
            elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
          } else if (listType === 'ul') {
            elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
          }
          currentListItems = [];
          listType = null;
        }
        // Skip code blocks for now
      } 
      // Handle empty lines
      else if (line.trim() === '') {
        if (currentListItems.length > 0) {
          if (listType === 'ol') {
            elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
          } else if (listType === 'ul') {
            elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
          }
          currentListItems = [];
          listType = null;
        }
        elements.push(<div key={index} className="h-2"></div>);
      } 
      // Handle regular paragraphs with bold text
      else {
        if (currentListItems.length > 0) {
          if (listType === 'ol') {
            elements.push(<ol key={`list-${index}`} className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
          } else if (listType === 'ul') {
            elements.push(<ul key={`list-${index}`} className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
          }
          currentListItems = [];
          listType = null;
        }
        // Parse bold text (**text**)
        const boldRegex = /\*\*([^*]+)\*\*/g;
        const textElements = [];
        let lastIndex = 0;
        let match;
        
        while ((match = boldRegex.exec(line)) !== null) {
          // Add text before the bold part
          if (match.index > lastIndex) {
            textElements.push(line.slice(lastIndex, match.index));
          }
          // Add the bold text
          textElements.push(<strong key={`bold-${match.index}`} className="font-semibold text-gray-800">{match[1]}</strong>);
          lastIndex = match.index + match[0].length;
        }
        
        // Add any remaining text
        if (lastIndex < line.length) {
          textElements.push(line.slice(lastIndex));
        }
        
        elements.push(
          <p key={index} className="text-gray-600 mb-2">
            {textElements}
          </p>
        );
      }
    });

    // Close any remaining list
    if (currentListItems.length > 0) {
      if (listType === 'ol') {
        elements.push(<ol key="final-list" className="ml-6 mb-4 list-decimal space-y-1">{currentListItems}</ol>);
      } else if (listType === 'ul') {
        elements.push(<ul key="final-list" className="ml-6 mb-4 list-disc space-y-1">{currentListItems}</ul>);
      }
    }

    return elements;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Documentation Library</h1>
            <p className="text-gray-600">Comprehensive guides and references for using Enable.AI</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.print()}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Copy className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
          />
        </div>
      </div>

      {/* Documentation Categories */}
      <div className="space-y-6">
        {Object.entries(filteredDocs).map(([categoryKey, category]) => (
          <div key={categoryKey} className="bg-white rounded-lg border border-gray-200">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(categoryKey)}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${category.bgColor}`}>
                  <div className={category.color}>
                    {category.icon}
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{category.title}</h2>
                  <p className="text-sm text-gray-500">{category.items.length} detailed walkthroughs</p>
                </div>
              </div>
              {expandedCategories.has(categoryKey) ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Category Content */}
            {expandedCategories.has(categoryKey) && (
              <div className="border-t border-gray-200">
                <div className="p-6 space-y-6">
                  {category.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-gray-600 mb-3">{item.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {item.tags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={() => copyToClipboard(item.content)}
                          className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          {copiedText === item.content ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                          <span>{copiedText === item.content ? 'Copied!' : 'Copy'}</span>
                        </button>
                      </div>
                      
                      <div className="prose prose-sm max-w-none">
                        {renderContent(item.content)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {Object.keys(filteredDocs).length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documentation found</h3>
          <p className="text-gray-600">Try adjusting your search terms or browse all categories.</p>
        </div>
      )}
    </div>
  );
};

export default DocumentationLibrary; 